import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, BedDouble, CalendarDays, Camera, Dumbbell, Flag, Heart,
  HelpCircle, ImageOff, Mail, MapPin, Mountain, Play, Route, Send,
  Share2, ShieldCheck, Sparkles, Star, Train, Users, Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { Btn } from "@/components/Primitives";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import FieldGuideItinerary, { organizerSocialActions } from "@/components/participant/FieldGuideItinerary";
import OrganizerContactDetails from "@/components/participant/OrganizerContactDetails";
import AssignedGuideProfiles from "@/components/participant/AssignedGuideProfiles";
import { visibleGuideProfiles } from "@/lib/visibleGuideProfiles";
import OrganizerFollowButton from "@/components/traveller/OrganizerFollowButton";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, formatApiErrorDetail, setActiveOrg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { batchUnavailable, participantTripPath } from "@/lib/departures";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function money(value, currency = "INR") {
  if (value === null || value === undefined || value === "") return "";
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(number / 100);
}

function dateRange(trip) {
  if (trip?.start_date || trip?.end_date) {
    return [trip.start_date, trip.end_date].filter(Boolean).map(fmtDate).filter((value) => value && value !== "-").join(" - ");
  }
  return trip?.season || trip?.dates?.season || "";
}

function uniqueText(items = []) {
  const seen = new Set();
  return asArray(items).map((item) => String(item || "").trim()).filter(Boolean).filter((item) => {
    const key = item.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function safeTitle(value, fallback) {
  return String(value || "").trim() || fallback;
}

const SUPPORTED_DAY_LAYOUTS = new Set([
  "image_text", "full_bleed_image", "map_first", "timeline", "gallery",
  "stats_focus", "activity_focus", "elevation_focus", "split_story", "quote",
]);

function dayLayoutType(value) {
  return SUPPORTED_DAY_LAYOUTS.has(value) ? value : "timeline";
}

function allPreviewPhotos(experience) {
  return asArray(experience?.days).flatMap((day) => [
    ...asArray(day.photos),
    ...asArray(day.activity_refs).flatMap((activity) => asArray(activity.photos)),
  ]).filter((photo) => photo?.url);
}

function coverUrl(data) {
  return data?.itinerary_experience?.hero_photo?.url
    || data?.cover_image?.url
    || data?.gallery?.find((item) => item.is_cover)?.url
    || allPreviewPhotos(data?.itinerary_experience)[0]?.url
    || "";
}

function firstDayPhoto(day) {
  return asArray(day?.photos).find((photo) => photo?.url)
    || asArray(day?.activity_refs).flatMap((activity) => asArray(activity.photos)).find((photo) => photo?.url)
    || null;
}

function publicFactValue(preview, trip, key) {
  const map = {
    destination: preview?.destination || trip.destination || trip.region,
    duration: preview?.duration || (trip.duration_days ? `${trip.duration_days} days` : ""),
    difficulty: preview?.difficulty || trip.difficulty_grade,
    distance: preview?.distance,
    elevation: preview?.elevation,
    season: preview?.season || dateRange(trip),
    group_size: preview?.group_size,
  };
  return map[key] || "";
}

function SafeImage({ src, alt = "", className = "", children }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className={`flex items-center justify-center bg-[#17211D] text-[#FFFAF1]/70 ${className}`}>
        <div className="text-center">
          <ImageOff className="mx-auto h-8 w-8" aria-hidden="true" />
          {children}
        </div>
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} loading="lazy" onError={() => setFailed(true)} />;
}

function RouteJourney({ experience, activeDay, setActiveDay }) {
  const days = asArray(experience?.days);
  if (!days.length) return null;
  return (
    <section className="tp-dark-panel overflow-hidden rounded-[2rem] p-5 text-[#FFFAF1]" aria-label="Animated route journey">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-black ring-1 ring-white/15"><Route className="h-4 w-4 text-[#E5B85C]" /> Route journey</p>
          <h2 className="mt-3 font-[Plus_Jakarta_Sans,sans-serif] text-3xl font-black leading-tight">Follow the trip chapter by chapter</h2>
          {experience?.route_story && <p className="mt-2 max-w-3xl text-sm leading-6 text-[#FFFAF1]/75">{experience.route_story}</p>}
        </div>
        <Btn type="button" variant="secondary" onClick={() => setActiveDay(days[0]?.day_number)}><Play className="h-4 w-4" /> Play journey</Btn>
      </div>
      <div className="mt-5 overflow-x-auto rounded-2xl bg-white/10 p-4 ring-1 ring-white/15">
        <div className="relative min-h-32" style={{ minWidth: days.length > 5 ? `${days.length * 7}rem` : "100%" }}>
          <svg className="absolute inset-x-0 top-2 h-16 w-full opacity-95" viewBox="0 0 760 90" preserveAspectRatio="none" aria-hidden="true">
            <path d="M18 56 C140 10 228 82 352 46 S580 12 742 54" fill="none" stroke="rgba(255,250,241,0.22)" strokeWidth="12" strokeLinecap="round" />
            <path className="tp-ai-route-flow" d="M18 56 C140 10 228 82 352 46 S580 12 742 54" fill="none" stroke="#E5B85C" strokeWidth="4" strokeLinecap="round" />
          </svg>
          <div className="relative grid gap-4 pt-12" style={{ gridTemplateColumns: `repeat(${days.length}, minmax(5rem, 1fr))` }}>
            {days.map((day, index) => {
              const selected = activeDay === day.day_number;
              return (
                <button
                  key={`${day.day_number}-${index}`}
                  type="button"
                  className="min-w-0 rounded-xl p-2 text-center transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B85C]"
                  aria-pressed={selected}
                  onClick={() => setActiveDay(day.day_number)}
                >
                  <span className={`tp-ai-route-ping mx-auto flex h-11 w-11 items-center justify-center rounded-full text-xs font-black ${selected ? "bg-[#E5B85C] text-[#17211D]" : "bg-[#FFFAF1] text-[#17211D]"}`} style={{ animationDelay: `${index * 120}ms` }}>D{day.day_number}</span>
                  <span className="mt-2 block truncate text-xs font-bold text-white/85">{day.headline || `Day ${day.day_number}`}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function DayChapter({ day, index, active, onFocus }) {
  const photo = firstDayPhoto(day);
  const photos = asArray(day.photos).filter((item) => item?.url);
  const activities = asArray(day.activity_refs);
  const highlights = uniqueText(day.highlights).slice(0, 5);
  const layout = dayLayoutType(day.layout_type);
  const imageFirst = ["map_first", "full_bleed_image", "gallery"].includes(layout) || (layout === "split_story" && index % 2 === 1);
  const fullBleed = layout === "full_bleed_image";
  return (
    <article
      className={`tp-ai-day-card overflow-hidden rounded-[2rem] border ${active ? "border-[#2D7A65]" : "border-[#E2DFD7]"} bg-[#FFFAF1] shadow-xl`}
      data-testid={`public-itinerary-day-${day.day_number}`}
      data-layout={layout}
      style={{ animationDelay: `${Math.min(index * 80, 560)}ms` }}
      tabIndex={-1}
      onFocus={onFocus}
    >
      <div className={`${fullBleed ? "grid" : "grid lg:grid-cols-[minmax(0,1fr)_minmax(20rem,0.46fr)]"} ${imageFirst ? "lg:[&>aside]:order-first" : ""}`}>
        <div className={`${fullBleed ? "order-2" : ""} p-5 sm:p-7`}>
          <div className="flex flex-col gap-4 border-b border-[#17211D]/10 pb-5 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 gap-3">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#E5B85C] text-sm font-black text-[#17211D] shadow-sm">D{day.day_number}</span>
              <div className="min-w-0">
                <p className="text-xs font-black uppercase text-[#516259]">Chapter {index + 1}</p>
                <h3 className="font-[Plus_Jakarta_Sans,sans-serif] text-2xl font-black leading-tight text-[#17211D]">{safeTitle(day.headline, `Day ${day.day_number}`)}</h3>
                {day.movement && <p className="mt-1 text-xs font-bold text-[#2D7A65]">{day.movement}</p>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {day.travel_mode && <span className="rounded-full bg-[#2D7A65] px-3 py-1 text-xs font-black capitalize text-white">{day.travel_mode}</span>}
              <span className="rounded-full bg-[#F7F1E5] px-3 py-1 text-xs font-black uppercase text-[#516259]">{layout.replace("_", " ")}</span>
            </div>
          </div>

          {day.story && <p className="mt-5 max-w-3xl text-sm leading-7 text-slate-700">{day.story}</p>}
          {highlights.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {highlights.map((item) => <span key={item} className="rounded-full bg-[#E5B85C]/20 px-3 py-1 text-xs font-black text-[#6D4B13]">{item}</span>)}
            </div>
          )}

          {activities.length > 0 && (
            <div className="mt-5 rounded-2xl border border-[#E2DFD7] bg-white/75 p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <p className="flex items-center gap-2 text-sm font-black text-[#17211D]"><Flag className="h-4 w-4 text-[#2D7A65]" /> Day flow</p>
                <span className="text-xs font-bold text-slate-500">{activities.length} public stop{activities.length === 1 ? "" : "s"}</span>
              </div>
              <div className="space-y-3">
                {activities.slice(0, 6).map((activity, activityIndex) => (
                  <div key={`${activity.title}-${activityIndex}`} className="grid gap-3 rounded-xl border border-[#E2DFD7] bg-[#FFFCF7] p-3 text-sm sm:grid-cols-[2rem_minmax(0,1fr)]">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2D7A65]/10 text-xs font-black text-[#2D7A65]">{activityIndex + 1}</span>
                    <div className="min-w-0">
                      <p className="font-bold text-[#17211D]">{safeTitle(activity.title, "Activity")}</p>
                      {activity.description && <p className="mt-1 text-xs leading-5 text-slate-600">{activity.description}</p>}
                      <p className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                        {activity.time && <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" />{activity.time}</span>}
                        {activity.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{activity.location}</span>}
                        {asArray(activity.photos).length > 0 && <span className="inline-flex items-center gap-1"><Camera className="h-3 w-3" />{activity.photos.length} photo{activity.photos.length === 1 ? "" : "s"}</span>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={`mt-5 grid gap-3 ${layout === "stats_focus" ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"}`}>
            {uniqueText(day.packing).length > 0 && <InfoTile icon={Camera} label="Carry" value={uniqueText(day.packing).join(" - ")} />}
            {uniqueText(day.safety).length > 0 && <InfoTile icon={ShieldCheck} label="Care" value={uniqueText(day.safety).join(" - ")} />}
            {day.movement && <InfoTile icon={Route} label="Route" value={day.movement} />}
            {layout === "activity_focus" && activities.length > 0 && <InfoTile icon={Flag} label="Public stops" value={`${activities.length} highlighted stops`} />}
            {layout === "elevation_focus" && day.movement && <InfoTile icon={Mountain} label="Elevation" value={day.movement} />}
          </div>
        </div>

        <aside className={`relative overflow-hidden bg-[#17211D] ${fullBleed ? "min-h-[24rem]" : "min-h-[22rem]"}`}>
          <SafeImage src={photo?.url} alt={photo?.alt_text || ""} className={`h-full w-full object-cover opacity-90 ${fullBleed ? "min-h-[24rem]" : "min-h-[22rem]"}`}>
            <p className="mt-2 text-xs font-bold">No public photo selected</p>
          </SafeImage>
          {layout === "gallery" && photos.length > 1 && (
            <div className="absolute inset-x-0 bottom-20 grid grid-cols-3 gap-2 px-4">
              {photos.slice(1, 4).map((item) => (
                <SafeImage key={item.id || item.url} src={item.url} alt={item.alt_text || ""} className="aspect-[4/3] rounded-xl object-cover ring-2 ring-white/70" />
              ))}
            </div>
          )}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-5 text-white">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-[#E5B85C]">Scene {String(index + 1).padStart(2, "0")}</p>
            <p className="mt-1 text-lg font-black">{safeTitle(day.headline, `Day ${day.day_number}`)}</p>
          </div>
        </aside>
      </div>
    </article>
  );
}

function InfoTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-[#F7F1E5] p-4 text-sm ring-1 ring-[#E2DFD7]">
      <p className="mb-2 flex items-center gap-2 text-xs font-black uppercase text-[#516259]"><Icon className="h-4 w-4 text-[#2D7A65]" /> {label}</p>
      <p className="leading-6 text-[#17211D]">{value}</p>
    </div>
  );
}

function PracticalDetails({ experience }) {
  const details = [
    experience?.transport_summary ? { label: "Getting around", icon: Train, value: experience.transport_summary } : null,
    experience?.accommodation_summary ? { label: "Where you will stay", icon: BedDouble, value: experience.accommodation_summary } : null,
    asArray(experience?.public_packing_preview).length ? { label: "What to carry", icon: Camera, value: uniqueText(experience.public_packing_preview).join(" - ") } : null,
    experience?.fitness_guidance ? { label: "Fitness level", icon: Dumbbell, value: experience.fitness_guidance } : null,
    experience?.public_safety_summary ? { label: "Safety", icon: ShieldCheck, value: experience.public_safety_summary } : null,
  ].filter(Boolean);
  if (!details.length && !asArray(experience?.public_faqs).length) return null;
  return (
    <section className="tp-paper rounded-[2rem] p-5 sm:p-6" aria-label="Practical details">
      <h2 className="font-[Plus_Jakarta_Sans,sans-serif] text-2xl font-black text-[#17211D]">Practical details</h2>
      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        {details.map((item) => {
          const Icon = item.icon;
          return (
            <details key={item.label} className="rounded-2xl border border-[#E2DFD7] bg-[#FFFCF7] p-4" open>
              <summary className="flex cursor-pointer items-center gap-2 text-sm font-black text-[#17211D]"><Icon className="h-4 w-4 text-[#2D7A65]" /> {item.label}</summary>
              <p className="mt-3 text-sm leading-6 text-slate-700">{item.value}</p>
            </details>
          );
        })}
        {asArray(experience?.public_faqs).slice(0, 6).map((faq, index) => (
          <details key={`${faq.question}-${index}`} className="rounded-2xl border border-[#E2DFD7] bg-[#FFFCF7] p-4">
            <summary className="flex cursor-pointer items-center gap-2 text-sm font-black text-[#17211D]"><HelpCircle className="h-4 w-4 text-[#2D7A65]" /> {faq.question || "Question"}</summary>
            {faq.answer && <p className="mt-3 text-sm leading-6 text-slate-700">{faq.answer}</p>}
          </details>
        ))}
      </div>
    </section>
  );
}

function TrustSection({ organization, guides, reviews }) {
  const leadGuide = asArray(guides).find((guide) => guide?.is_lead) || asArray(guides)[0];
  const reviewItems = asArray(reviews?.data);
  const rating = reviews?.summary?.average_rating ?? reviews?.summary?.rating;
  if (!organization && !leadGuide && !reviewItems.length) return null;
  return (
    <section className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]" aria-label="Organization and reviews">
      <div className="tp-paper rounded-[2rem] p-5 sm:p-6">
        <p className="text-xs font-black uppercase text-[#516259]">Hosted by</p>
        <div className="mt-3 flex gap-3">
          {organization?.logo && <SafeImage src={organization.logo} alt="" className="h-16 w-16 shrink-0 rounded-2xl object-cover" />}
          <div>
            <h2 className="font-[Plus_Jakarta_Sans,sans-serif] text-2xl font-black text-[#17211D]">{organization?.display_name || "TrailPulse organizer"}</h2>
            {(organization?.bio || organization?.full_description) && <p className="mt-2 text-sm leading-6 text-slate-700">{organization.bio || organization.full_description}</p>}
            <OrganizerContactDetails organization={organization || {}} />
            <nav className="mt-3 flex flex-wrap gap-3" aria-label="Organizer links">
              {organizerSocialActions(organization).map(({ key, icon: Icon, label, href }) => <a key={key} className="inline-flex min-h-9 items-center gap-1.5 text-sm font-bold text-[#2D7A65]" href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"><Icon className="h-4 w-4" />{label}</a>)}
            </nav>
          </div>
        </div>
        {leadGuide && (
          <div className="mt-5 rounded-2xl bg-[#F7F1E5] p-4">
            <p className="mb-3 text-xs font-black uppercase text-[#516259]">Guides</p>
            <AssignedGuideProfiles profiles={visibleGuideProfiles(guides, "public")} />
          </div>
        )}
      </div>

      <div className="tp-paper rounded-[2rem] p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-[Plus_Jakarta_Sans,sans-serif] text-2xl font-black text-[#17211D]">Traveller voices</h2>
          {rating && <span className="inline-flex items-center gap-1 rounded-full bg-[#E5B85C]/20 px-3 py-1 text-xs font-black text-[#6D4B13]"><Star className="h-4 w-4" /> {Number(rating).toFixed(1)}</span>}
        </div>
        {reviewItems.length ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {reviewItems.slice(0, 4).map((review) => (
              <article key={review.id} className="rounded-2xl bg-[#F7F1E5] p-4 text-sm text-slate-600">
                <p className="font-black text-[#17211D]">{review.display_name || "Participant"}</p>
                {(review.testimonial_text || review.review_text || review.comment) && <p className="mt-2 leading-6">{review.testimonial_text || review.review_text || review.comment}</p>}
              </article>
            ))}
          </div>
        ) : <p className="mt-4 text-sm text-slate-600">Reviews will appear here once the organizer publishes approved testimonials.</p>}
      </div>
    </section>
  );
}

function PublicItinerary({ experience, activeDay, setActiveDay }) {
  const storyDays = asArray(experience?.days);
  if (!experience || !storyDays.length) {
    return <EmptyState icon={Route} title="No public itinerary yet" description="This trip has not published an approved itinerary preview." />;
  }
  return (
    <section className="space-y-5" aria-label="Published public itinerary">
      <RouteJourney experience={experience} activeDay={activeDay} setActiveDay={setActiveDay} />
      <div className="space-y-5">
        {storyDays.map((day, index) => (
          <DayChapter
            key={`${day.day_number}-${index}`}
            day={day}
            index={index}
            active={activeDay === day.day_number}
            onFocus={() => setActiveDay(day.day_number)}
          />
        ))}
      </div>
    </section>
  );
}

export default function PublicTripDetail() {
  const { tripId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeDay, setActiveDay] = useState(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const [detail, reviewResp] = await Promise.all([
        api.get(`/public/trips/${tripId}`),
        api.get(`/public/trips/${tripId}/reviews`),
      ]);
      setData(detail.data);
      setReviews(reviewResp.data);
      setActiveDay(asArray(detail.data?.itinerary_experience?.days)[0]?.day_number || null);
    } catch (err) {
      setError(formatApiError(err, "Could not load public trip."));
    }
  }, [tripId]);

  useEffect(() => { load(); }, [load]);

  const trip = useMemo(() => data?.trip || {}, [data?.trip]);
  useEffect(() => {
    if (user?.account_type !== "traveller" || !trip.id) return;
    let alive = true;
    setSaved(false);
    api.get("/traveller/wishlist", { params: { public_trip_id: trip.id, page_size: 1 } })
      .then(({ data }) => { if (alive) setSaved(Boolean(data.data?.length)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user?.account_type, user?.id, trip.id]);
  const experience = data?.itinerary_experience;
  const image = coverUrl(data);
  const priceText = money(trip.base_price_minor ?? trip.price_minor ?? trip.price_per_pax_minor, trip.currency || "INR");
  const publicBatches = asArray(trip.batches);
  const linkedBatchId = new URLSearchParams(window.location.search).get("batch");
  const selectedBatch = publicBatches.find((batch) => String(batch.id) === String(linkedBatchId)) || publicBatches.find((batch) => !batchUnavailable(batch)) || publicBatches[0] || null;
  const factCards = useMemo(() => [
    { label: "Destination", icon: MapPin, value: publicFactValue(experience, trip, "destination") },
    { label: "Duration", icon: CalendarDays, value: publicFactValue(experience, trip, "duration") },
    { label: "Difficulty", icon: Mountain, value: publicFactValue(experience, trip, "difficulty") },
    { label: "Route", icon: Route, value: uniqueText([publicFactValue(experience, trip, "distance"), publicFactValue(experience, trip, "elevation")]).join(" - ") },
    { label: "Season", icon: CalendarDays, value: publicFactValue(experience, trip, "season") },
    { label: "Group", icon: Users, value: publicFactValue(experience, trip, "group_size") },
    { label: "Price", icon: Sparkles, value: priceText },
    { label: "Availability", icon: Users, value: trip.availability || experience?.availability_reference },
  ].filter((item) => item.value), [experience, trip, priceText]);

  const addToWishlist = async () => {
    if (!user) {
      nav(`/login?next=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`);
      return;
    }
    setBusy(true);
    try {
      await api.post(user.account_type === "traveller" ? "/traveller/wishlist" : "/participants/me/wishlist", {
        public_trip_id: trip.id,
        destination: trip.destination || trip.region || "",
        title: trip.title,
        notes: "[type: public_trip]",
        priority: "medium",
        status: "saved",
        visibility: "private",
      });
      toast.success("Trip added to wishlist");
      setSaved(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const shareTrip = async () => {
    const shareData = { title: experience?.title || trip.title, text: experience?.subtitle || trip.summary || "", url: window.location.href };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch {
        return;
      }
    }
    await navigator.clipboard?.writeText(window.location.href);
    toast.success("Trip link copied");
  };
  const enquire = async () => {
    const name = window.prompt("Your name");
    if (!name) return;
    const phone = window.prompt("Phone number for the organizer to contact you");
    if (!phone) return;
    const slots = window.prompt("Number of slots (required)", "");
    if (slots === null) return;
    const pax = Number(slots);
    if (!Number.isInteger(pax) || pax < 1) { toast.error("Enter a whole number of slots, at least 1."); return; }
    const email = window.prompt("Email (optional)", "");
    const message = window.prompt("Anything the organizer should know?", "");
    setBusy(true);
    try {
      await api.post(`/public/trips/${trip.id}/enquiries`, { name, phone, pax, email: email || null, message: message || "", batch_id: selectedBatch?.id || null, waitlist: ["sold_out", "closed"].includes(selectedBatch?.status) });
      toast.success("Enquiry sent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  // Trip booking paused until Razorpay integration. Original handler retained.
// const payDeposit = async () => {
//     if (!user) {
//       nav(`/login?next=${encodeURIComponent(`${window.location.pathname}${window.location.search || ""}`)}`);
//       return;
//     }
//     setBusy(true);
//     try {
//       const { data } = await api.post(`/public/trips/${trip.id}/reserve`, { pax: 1, batch_id: selectedBatch?.id || null });
//       if (data.active_org_id) setActiveOrg(data.active_org_id);
//       toast.success("Reservation created. Payment is pending.");
//       nav(participantTripPath(trip.id, data.participant_id));
//     } catch (err) {
//       toast.error(formatApiErrorDetail(err.response?.data?.detail));
//     } finally {
//       setBusy(false);
//     }
//   };

  if (error) return <main className="tp-app-shell min-h-screen p-4 sm:p-6"><ErrorState message={error} onRetry={load} /></main>;
  if (!data) return <main className="tp-app-shell min-h-screen p-4 sm:p-6"><LoadingState label="Loading public trip..." /></main>;

  if (trip.state === "published") {
    return (
      <>
      <FieldGuideItinerary
        trip={data.trip}
        days={data.days}
        guides={data.guides}
        gallery={data.gallery}
        coverImage={data.cover_image}
        heroVideo={data.hero_video || data.trip?.hero_video}
        operationalSummary={data.operational_summary}
        organization={data.organization_profile}
        packingItems={data.packing_items}
        audience="public"
        onBooked={load}
        testId="public-trip-detail"
      />
      {user?.account_type === "traveller" && (
        <div className="traveller-public-save">
          {saved ? <Link to="/traveller/wishlist"><Heart className="h-4 w-4 fill-current" /> Saved to wishlist</Link> : <button type="button" disabled={busy} onClick={addToWishlist}><Heart className="h-4 w-4" /> {busy ? "Saving..." : "Save this trip"}</button>}
          <OrganizerFollowButton organizer={{ id: data.organization_id || data.organization_profile?.org_id, display_name: data.organization_profile?.display_name }} />
        </div>
      )}
      </>
    );
  }

  return (
    <main className="tp-app-shell min-h-screen p-4 text-[#1A2420] sm:p-6" data-testid="public-trip-detail">
      <div className="mx-auto max-w-7xl space-y-6">
        <Link to="/discover" className="inline-flex min-h-10 items-center gap-2 rounded-xl px-3 text-sm font-bold text-[#2D7A65] hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65]"><ArrowLeft className="h-4 w-4" /> Discovery</Link>

        <section className="relative overflow-hidden rounded-[2rem] bg-[#17211D] text-[#FFFAF1] shadow-xl">
          <SafeImage src={image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#17211D]/96 via-[#17211D]/72 to-[#17211D]/25" />
          <div className="relative min-h-[34rem] p-6 sm:p-8 lg:p-10">
            <div className="flex h-full min-h-[30rem] flex-col justify-between">
              <div>
                {(experience?.destination || trip.destination || trip.region) && <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.26em] text-[#E5B85C]">{experience?.destination || trip.destination || trip.region}</p>}
                <h1 className="max-w-4xl font-[Plus_Jakarta_Sans,sans-serif] text-4xl font-black leading-none sm:text-6xl lg:text-7xl">{experience?.title || trip.title}</h1>
                {(experience?.subtitle || experience?.summary || trip.summary) && <p className="mt-5 max-w-2xl text-base leading-7 text-[#FFFAF1]/84">{experience?.subtitle || experience?.summary || trip.summary}</p>}
              </div>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Btn onClick={() => document.querySelector("[data-testid^='public-itinerary-day-']")?.scrollIntoView({ behavior: "smooth", block: "start" })}><Route className="h-4 w-4" /> Explore the journey</Btn>
                <Btn onClick={enquire} disabled={busy} variant="secondary"><Mail className="h-4 w-4" /> Enquire</Btn>
                {/* Trip booking paused until Razorpay integration.
<Btn onClick={payDeposit} disabled={busy} variant="secondary"><Wallet className="h-4 w-4" /> Reserve spot</Btn>
*/}
                <Btn onClick={addToWishlist} disabled={busy} variant="secondary"><Heart className="h-4 w-4" /> Add to wishlist</Btn>
                <Btn onClick={shareTrip} variant="secondary"><Share2 className="h-4 w-4" /> Share</Btn>
              </div>
            </div>
          </div>
        </section>

        {factCards.length > 0 && (
          <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" aria-label="Public trip facts">
            {factCards.map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.label} className="tp-panel p-4">
                  <Icon className="mb-2 h-5 w-5 text-[#2D7A65]" aria-hidden="true" />
                  <span className="block text-xs font-extrabold uppercase text-[#516259]">{card.label}</span>
                  <strong className="mt-1 block text-[#17211D]">{card.value}</strong>
                </div>
              );
            })}
          </section>
        )}

        {(experience?.summary || asArray(experience?.highlights).length || asArray(experience?.what_makes_special).length) && (
          <section className="tp-paper rounded-[2rem] p-5 sm:p-6">
            <div className="grid gap-5 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
              <div>
                <p className="text-xs font-black uppercase text-[#516259]">The story</p>
                <h2 className="mt-2 font-[Plus_Jakarta_Sans,sans-serif] text-3xl font-black text-[#17211D]">{safeTitle(experience?.title, trip.title)}</h2>
                {experience?.summary && <p className="mt-3 text-sm leading-7 text-slate-700">{experience.summary}</p>}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {uniqueText([...asArray(experience?.highlights), ...asArray(experience?.what_makes_special)]).slice(0, 6).map((item) => (
                  <div key={item} className="rounded-2xl bg-[#F7F1E5] p-4 text-sm font-semibold leading-6 text-[#17211D] ring-1 ring-[#E2DFD7]">{item}</div>
                ))}
              </div>
            </div>
          </section>
        )}

        <PublicItinerary experience={experience} activeDay={activeDay} setActiveDay={setActiveDay} />
        <PracticalDetails experience={experience} />
        <TrustSection organization={data?.organization_profile} guides={data?.guides} reviews={reviews} />

        <section className="tp-dark-panel rounded-[2rem] p-6 text-[#FFFAF1] sm:p-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#E5B85C]">Ready when you are</p>
              <h2 className="mt-2 font-[Plus_Jakarta_Sans,sans-serif] text-3xl font-black">Your next great story starts here</h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-white/75">{experience?.enquiry_cta_copy || experience?.public_cta || "Ask the organizer about dates, suitability, and availability."}</p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Btn onClick={addToWishlist} disabled={busy}><Heart className="h-4 w-4" /> Save</Btn>
              <Btn onClick={enquire} disabled={busy} variant="secondary"><Mail className="h-4 w-4" /> Enquire</Btn>
              {/* Trip booking paused until Razorpay integration.
<Btn onClick={payDeposit} disabled={busy} variant="secondary"><Wallet className="h-4 w-4" /> Reserve spot</Btn>
*/}
              <a className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-white/20 bg-white px-4 text-sm font-bold text-[#17211D] shadow-sm hover:bg-[#FFFAF1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B85C]" href={`mailto:?subject=${encodeURIComponent(experience?.title || trip.title || "TrailPulse trip")}&body=${encodeURIComponent(window.location.href)}`}><Mail className="h-4 w-4" /> Invite friends</a>
              <Btn onClick={shareTrip} variant="secondary"><Send className="h-4 w-4" /> Ask a question</Btn>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
